import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Checkbox,
  Text,
  Card,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { AppsRegular } from '@fluentui/react-icons';
import { EmployeeUpdateFormData } from '../../../hooks/useEmployeeUpdateForm';
import { apiClient, ListResponse } from '../../../api/client';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  intro: {
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  softwareItem: {
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalM,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  icon: {
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: tokens.colorNeutralForeground3,
  },
  content: {
    flex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

interface SoftwareStepProps {
  formData: EmployeeUpdateFormData;
  updateFormData: (updates: Partial<EmployeeUpdateFormData>) => void;
}

interface Software {
  _id: string;
  name: string;
  description: string;
  category?: string;
  requiresLicense: boolean;
}

export function SoftwareStep({ formData, updateFormData }: SoftwareStepProps) {
  const styles = useStyles();
  const [software, setSoftware] = useState<Software[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<ListResponse<Software>>('/software')
      .then((response) => setSoftware(response.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const toggleSoftware = (id: string) => {
    const isSelected = formData.requestedSoftware.includes(id);
    const newSoftware = isSelected
      ? formData.requestedSoftware.filter((swId) => swId !== id)
      : [...formData.requestedSoftware, id];

    updateFormData({ requestedSoftware: newSoftware });
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading software..." />
      </div>
    );
  }

  if (software.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text>No software available</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Text size={500} weight="semibold">
        Software Requests
      </Text>
      <Text className={styles.intro}>
        Select any additional software the employee needs. These requests are informational and will be reviewed by administrators.
      </Text>

      {software.map((sw) => (
        <Card
          key={sw._id}
          className={styles.softwareItem}
          onClick={() => toggleSoftware(sw._id)}
        >
          <div className={styles.icon}>
            <AppsRegular />
          </div>
          <div className={styles.content}>
            <div className={styles.header}>
              <Text className={styles.title}>{sw.name}</Text>
              {sw.requiresLicense && (
                <Badge appearance="tint" color="warning">
                  License Required
                </Badge>
              )}
            </div>
            <Text className={styles.description}>{sw.description}</Text>
          </div>
          <Checkbox checked={formData.requestedSoftware.includes(sw._id)} />
        </Card>
      ))}
    </div>
  );
}
